import { Box, createStyles, keyframes } from '@mantine/core';
import { useLocalStorageBooleanState } from '@repo/common/hooks/useLocalStorageBooleanState';
import { Banner } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';

const slideUp = keyframes({
	from: {
		transform: 'translateY(100%)',
	},
	to: {
		transform: 'translateY(0)',
	},
});

const useStyles = createStyles(() => ({
	bannerContainer: {
		animation: `${slideUp} 0.3s ease-out`,
	},
}));

enum BannerType {
	EDUCATION = 'education',
	MESSAGE_LIMIT = 'message_limit',
	CREDIT_RESTRICTION = 'credit_restriction',
}

const ChatBanner = observer(
	({
		showQuestionEducationBanner,
		showMessageLimitBanner,
		showCreditRestrictionBanner,
		creditRestrictionReason,
	}: {
		showQuestionEducationBanner: boolean;
		showMessageLimitBanner: boolean;
		showCreditRestrictionBanner: boolean;
		creditRestrictionReason?: string;
	}) => {
		const { classes } = useStyles();

		const [
			questionEducationBannerDismissed,
			{ setTrue: dismissQuestionEducationBanner },
		] = useLocalStorageBooleanState('questionEducationBannerShown', false);

		const [messageLimitBannerDismissed, setMessageLimitBannerDismissed] =
			useState(false);

		const canShowEducationBanner =
			!questionEducationBannerDismissed && showQuestionEducationBanner;

		const canShowMessageLimitBanner =
			!messageLimitBannerDismissed && showMessageLimitBanner;

		const canShowCreditRestrictionBanner = showCreditRestrictionBanner;

		const visibleBanner = useMemo(() => {
			if (canShowCreditRestrictionBanner) return BannerType.CREDIT_RESTRICTION;
			if (canShowEducationBanner) return BannerType.EDUCATION;
			if (canShowMessageLimitBanner) return BannerType.MESSAGE_LIMIT;
			return null;
		}, [
			canShowEducationBanner,
			canShowMessageLimitBanner,
			canShowCreditRestrictionBanner,
		]);

		const handleBannerDismiss = useCallback(
			(bannerType: BannerType) => {
				switch (bannerType) {
					case BannerType.EDUCATION:
						dismissQuestionEducationBanner();
						break;
					case BannerType.MESSAGE_LIMIT:
						setMessageLimitBannerDismissed(true);
						break;
					case BannerType.CREDIT_RESTRICTION:
						// Credit restriction banner cannot be dismissed
						break;
					default:
						break;
				}
			},
			[dismissQuestionEducationBanner, setMessageLimitBannerDismissed]
		);

		const content = useMemo(() => {
			switch (visibleBanner) {
				case BannerType.EDUCATION:
					return {
						title: "Not getting the answer you're looking for?",
						message:
							"If Secoda AI isn't providing the answer you need, you can forward your question to your team. Just select the question icon on a message to get started.",
					};
				case BannerType.MESSAGE_LIMIT:
					return {
						message:
							'After 10 messages inaccuracies may occur. Please consider starting a new chat to maintain high response quality.',
					};
				case BannerType.CREDIT_RESTRICTION:
					return {
						title: 'AI Access Restricted',
						message:
							creditRestrictionReason ||
							'Insufficient credit balance – add credits to continue using AI features',
						tone: 'warning' as const,
					};
				default:
					return null;
			}
		}, [visibleBanner, creditRestrictionReason]);

		if (!visibleBanner || !content) {
			return null;
		}

		return (
			<Box pb="xs" style={{ overflow: 'hidden' }}>
				<Box className={classes.bannerContainer}>
					<Banner
						tone={content.tone || 'neutral'}
						{...content}
						onDismiss={
							visibleBanner === BannerType.CREDIT_RESTRICTION
								? undefined
								: () => handleBannerDismiss(visibleBanner)
						}
					/>
				</Box>
			</Box>
		);
	}
);

export { ChatBanner };
